import { log } from './logger';

// TikTok writes its built-in emoji into chat text as [code]; its apps swap the code for an image, but
// the LIVE chat feed only gives us the text. Codes are TikTok's documented "secret emoji" list
// (https://blog.hootsuite.com/tiktok-secret-emojis/) plus codes seen in LIVE chat. TikTok draws its own
// artwork, so each maps to the closest standard emoji. Unknown codes stay as text and are logged in
// debug mode so new LIVE codes can be added here.
export const TIKTOK_EMOJI = {
  // Seen in LIVE chat (not on the comments list)
  laughcry: '😂',

  // Documented list
  smile: '🙂',
  happy: '😊',
  angry: '😠',
  cry: '😢',
  embarrassed: '😅',
  surprised: '😮',
  wronged: '🥺',
  shout: '😫',
  flushed: '😳',
  yummy: '😋',
  complacent: '😌',
  drool: '🤤',
  scream: '😱',
  weep: '😭',
  speechless: '😶',
  funnyface: '🤪',
  laughwithtears: '😂',
  wicked: '😈',
  facewithrollingeyes: '🙄',
  sulk: '😒',
  thinking: '🤔',
  lovely: '🥰',
  greedy: '🤑',
  wow: '🤩',
  joyful: '😁',
  hehe: '😝',
  slap: '👋',
  tears: '🥲',
  stun: '😵',
  cute: '🥹',
  blink: '😉',
  disdain: '😑',
  astonish: '😲',
  rage: '😡',
  cool: '😎',
  excited: '🤗',
  proud: '😏',
  smileface: '😃',
  evil: '👿',
  angel: '😇',
  laugh: '😆',
  pride: '😤',
  nap: '😴',
  loveface: '😍',
  awkward: '😬',
  shock: '😨',
};

// A chat message with its subscriber emotes put in place, then TikTok's [code] emoji:
// [{ type: 'text', text } | { type: 'emoji', code, emoji } | { type: 'subEmote', id, image }]
// emotes: [{ id, image, place }] from tiktok-chat-service, `place` being where in the text the image
// goes. Missing or out-of-range places go at the end rather than splitting the text oddly.
export function parseTikTokMessage(text, emotes = []) {
  const message = text || '';
  const placed = emotes
    .filter((e) => e?.image)
    .map((e) => ({ ...e, at: Number.isInteger(e.place) && e.place >= 0 && e.place <= message.length ? e.place : message.length }))
    .sort((a, b) => a.at - b.at);

  const pieces = [];
  let cursor = 0;
  placed.forEach((e) => {
    pieces.push(...parseTikTokEmoji(message.slice(cursor, e.at)));
    pieces.push({ type: 'subEmote', id: e.id, image: e.image });
    cursor = e.at;
  });
  pieces.push(...parseTikTokEmoji(message.slice(cursor)));
  return pieces;
}

const CODE_SPLIT = /(\[[a-z]+\])/i;
const loggedUnknown = new Set();

// Text -> [{ type: 'text', text } | { type: 'emoji', code, emoji }]
export function parseTikTokEmoji(text) {
  if (!text) return [];
  return text.split(CODE_SPLIT).filter(Boolean).map((part) => {
    const match = /^\[([a-z]+)\]$/i.exec(part);
    if (!match) return { type: 'text', text: part };
    const code = match[1].toLowerCase();
    const emoji = TIKTOK_EMOJI[code];
    if (emoji) return { type: 'emoji', code, emoji };
    if (!loggedUnknown.has(code)) {
      loggedUnknown.add(code);
      log.debug(`[TikTok] Unknown emoji code [${code}] - add it to TIKTOK_EMOJI in src/lib/tiktokEmoji.js`);
    }
    return { type: 'text', text: part };
  });
}
